import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TermsOfUse() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // 滚动到页面顶部
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>返回</span>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Terms of Use</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* 标题部分 */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Use</h1>
            <p className="text-gray-600">
              Welcome to our AI tool services. Please read the following terms of use carefully. 
              By using this service, you agree to accept and comply with these terms.
            </p>
          </div>

          {/* 公司信息 */}
          <div className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
            <div className="space-y-2 text-gray-700">
              <p><span className="font-medium">Company Name:</span> SmartTool AI Solutions Limited</p>
              <p><span className="font-medium">Brand Name:</span> AI Monkey</p>
              <p><span className="font-medium">Company Website:</span> <a href="https://ai2you.org/" className="text-orange-500 hover:text-orange-600">https://ai2you.org/</a></p>
              <p><span className="font-medium">Contact Email:</span> <a href="mailto:support@ai2you.org" className="text-orange-500 hover:text-orange-600">support@ai2you.org</a></p>
              <p className="text-sm text-gray-600 mt-4">These Terms of Use are effective as of May 15, 2025</p>
            </div>
          </div>

          {/* 条款内容 */}
          <div className="space-y-8">
            {/* 1. Definitions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Definitions</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>"Service":</strong> Refers to all products, features, and services provided by SmartTool AI Solutions Limited.</p>
                <p><strong>"User":</strong> Refers to individuals or entities that use or access the Service.</p>
                <p><strong>"Content":</strong> Refers to text, images, audio, video, code, or other materials uploaded, shared, created, or submitted by users through the Service.</p>
                <p><strong>"Generated Content":</strong> Refers to all output results generated through the Service's AI tools.</p>
              </div>
            </section>

            {/* 2. Service Usage */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Usage</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.1 Account Registration</h3>
                  <p className="text-gray-700">
                    Using certain service features requires account registration. You must provide accurate, 
                    complete, and current information, and secure your account credentials.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.2 Usage Restrictions</h3>
                  <p className="text-gray-700 mb-2">You agree not to:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Create, upload, or share illegal content using the Service</li>
                    <li>Attempt to circumvent the Service's technical limitations or usage restrictions</li>
                    <li>Engage in behavior that may harm the Service or other users' experience</li>
                    <li>Obtain information or data from other users without permission</li>
                    <li>Use the Service for any purpose that violates applicable laws and regulations</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">2.3 API Usage</h3>
                  <p className="text-gray-700">
                    Using our APIs requires compliance with additional API terms of use, including rate limits 
                    and authentication requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. Content and Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Content and Intellectual Property</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.1 User Content</h3>
                  <p className="text-gray-700">
                    You retain all rights to the content you upload, but grant us a worldwide license to store, 
                    use, and process your content to provide and improve the Service.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.2 Generated Content</h3>
                  <p className="text-gray-700 mb-2">
                    Content generated through our AI tools belongs to you, provided that you comply with the following conditions:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>You have legal rights to the input content provided to the AI tools</li>
                    <li>The generated content does not violate any laws, regulations, or third-party rights</li>
                    <li>You do not use the generated content for any illegal or harmful purpose</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">3.3 Service Intellectual Property</h3>
                  <p className="text-gray-700">
                    The Service and all its components (including but not limited to code, design, features, 
                    and documentation) belong to us or our licensors and are protected by intellectual property laws.
                  </p>
                </div>
              </div>
            </section>

            {/* 4. User Responsibilities */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Responsibilities</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.1 Lawful Use</h3>
                  <p className="text-gray-700">
                    You agree not to use the Service for any illegal or unauthorized activities. 
                    You are fully responsible for all actions taken while using the Service.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.2 Content Moderation</h3>
                  <p className="text-gray-700">
                    You understand that we may review or screen user-submitted content, but this does not 
                    constitute an obligation for us to comprehensively monitor all content.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">4.3 Indemnification</h3>
                  <p className="text-gray-700">
                    You agree to indemnify and hold us harmless from any claims, losses, or expenses resulting 
                    from your violation of these terms or infringement of others' rights.
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Service Changes and Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Service Changes and Termination</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.1 Service Changes</h3>
                  <p className="text-gray-700">
                    We reserve the right to modify, suspend, or discontinue the Service at any time without prior notice.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.2 Account Termination</h3>
                  <p className="text-gray-700">
                    We may terminate or suspend your account for violations of these terms or prolonged inactivity. 
                    You may also choose to terminate your account at any time.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">5.3 Termination Consequences</h3>
                  <p className="text-gray-700">
                    Upon termination, you may lose access to your account and related content. Certain terms survive 
                    termination, such as intellectual property, confidentiality, and indemnification provisions.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Disclaimers and Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disclaimers and Limitation of Liability</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.1 "As Is" Provision</h3>
                  <p className="text-gray-700">
                    The Service is provided on an "as is" and "as available" basis, without any warranties of any kind, 
                    express or implied, including but not limited to warranties of merchantability, fitness for a 
                    particular purpose, and non-infringement.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.2 Limitation of Liability</h3>
                  <p className="text-gray-700">
                    To the maximum extent permitted by law, we shall not be liable for any direct, indirect, incidental, 
                    special, consequential, or punitive damages arising from the use or inability to use the Service.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">6.3 Limitation Exceptions</h3>
                  <p className="text-gray-700">
                    Some jurisdictions do not allow the exclusion of certain warranties or limitation of certain types 
                    of liability, so the above limitations may not apply fully to you.
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Miscellaneous Provisions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Miscellaneous Provisions</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.1 Term Modifications</h3>
                  <p className="text-gray-700">
                    We reserve the right to modify these terms at any time. Modified terms will be posted on the website 
                    and take effect immediately upon posting. Your continued use of the Service constitutes acceptance 
                    of the modified terms.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.2 Entire Agreement</h3>
                  <p className="text-gray-700">
                    These terms constitute the entire agreement between you and us regarding the use of the Service, 
                    superseding all prior or contemporaneous oral or written communications.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.3 Severability</h3>
                  <p className="text-gray-700">
                    If any provision of these terms is found to be invalid or unenforceable, the remaining provisions 
                    will remain in full force and effect.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.4 Notices</h3>
                  <p className="text-gray-700">
                    We may send notices to you via email, in-service notifications, or website announcements. 
                    You agree that these electronic notices satisfy any legal communication requirements.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.5 Assignment</h3>
                  <p className="text-gray-700">
                    You may not assign your rights and obligations under these terms without our prior written consent. 
                    We may freely assign our rights and obligations.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">7.6 Governing Law</h3>
                  <p className="text-gray-700">
                    These terms are governed by the laws of the jurisdiction where SmartTool AI Solutions Limited 
                    is registered, without regard to conflict of law principles.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* 最后更新时间 */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600">Last Updated: May 15, 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
} 